import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function MyMarqueeAdmin() {
  const [marqueeText, setMarqueeText] = useState('');
  const [newMarqueeText, setNewMarqueeText] = useState('');
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchMarqueeText();
  }, []);

  const fetchMarqueeText = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/api/marquee`);
      setMarqueeText(response.data.marquee_text || '');
      setNewMarqueeText(response.data.marquee_text || '');
      setError(null);
    } catch (error) {
      setError('Error fetching marquee text');
      console.error('Fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateMarqueeText = async (e) => {
    e.preventDefault();
    
    if (!newMarqueeText.trim()) {
      setError('Marquee text cannot be empty');
      return;
    }

    try {
      setUpdating(true);
      setError('');
      setSuccess('');

      const response = await axios.post(`${API_BASE_URL}/api/marquee`, {
        marquee_text: newMarqueeText
      });

      setMarqueeText(newMarqueeText);
      setSuccess('Marquee text updated successfully!');
      
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError('Error updating marquee text');
      console.error('Update error:', error);
    } finally {
      setUpdating(false);
    }
  };

  const clearMarquee = async () => {
    try {
      setUpdating(true);
      setError('');
      
      const response = await axios.post(`${API_BASE_URL}/api/marquee`, {
        marquee_text: ''
      });

      setMarqueeText('');
      setNewMarqueeText('');
      setSuccess('Marquee text cleared successfully!');
      
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError('Error clearing marquee text');
      console.error('Clear error:', error);
    } finally {
      setUpdating(false);
    }
  };

  if (loading) {
    return (
      <div className="container mt-4">
        <div className="d-flex justify-content-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid mt-4">
      <div className="row justify-content-center">
        <div className="col-12 col-xl-10">
          <div className="card shadow-sm border-0">
            <div className="card-header bg-primary text-white py-3">
              <h4 className="mb-0 fw-bold">
                <i className="bi bi-megaphone me-2"></i>
                Marquee Text Management
              </h4>
            </div>
            <div className="card-body p-4">
              {/* Success Message */}
              {success && (
                <div className="alert alert-success alert-dismissible fade show d-flex align-items-center" role="alert">
                  <i className="bi bi-check-circle-fill me-2"></i>
                  <div>{success}</div>
                  <button type="button" className="btn-close ms-auto" onClick={() => setSuccess('')}></button>
                </div>
              )}

              {/* Error Message */}
              {error && (
                <div className="alert alert-danger alert-dismissible fade show d-flex align-items-center" role="alert">
                  <i className="bi bi-exclamation-triangle-fill me-2"></i>
                  <div>{error}</div>
                  <button type="button" className="btn-close ms-auto" onClick={() => setError(null)}></button>
                </div>
              )}

              {/* Current Marquee Display with Actual Marquee Effect */}
              <div className="mb-4">
                <h5 className="fw-semibold text-dark mb-3">
                  <i className="bi bi-eye me-2"></i>
                  Current Marquee Preview:
                </h5>
                {marqueeText.trim() ? (
                  <div className="alert alert-info border-0">
                    <strong className="d-block mb-2">Live Preview:</strong>
                    <div 
                      className="marquee-preview-container bg-white border rounded p-3 shadow-sm"
                      style={{ 
                        backgroundColor: '#f8f9fa',
                        border: '2px solid #dee2e6'
                      }}
                    >
                      <div 
                        className="marquee-content"
                        style={{
                          overflow: 'hidden',
                          whiteSpace: 'nowrap',
                          position: 'relative'
                        }}
                      >
                        <div 
                          className="marquee-text"
                          style={{
                            display: 'inline-block',
                            paddingLeft: '100%',
                            animation: 'marquee 15s linear infinite',
                            fontSize: '16px',
                            fontWeight: '600',
                            color: '#2c3e50'
                          }}
                        >
                          {marqueeText}
                        </div>
                      </div>
                    </div>
                    <style>
                      {`
                        @keyframes marquee {
                          0% { transform: translateX(0); }
                          100% { transform: translateX(-100%); }
                        }
                        .marquee-text:hover {
                          animation-play-state: paused;
                        }
                      `}
                    </style>
                  </div>
                ) : (
                  <div className="alert alert-warning d-flex align-items-center">
                    <i className="bi bi-info-circle me-2"></i>
                    No marquee text set. Add a new marquee text below.
                  </div>
                )}
              </div>

              {/* Update Form */}
              <form onSubmit={updateMarqueeText}>
                <div className="mb-4">
                  <label htmlFor="marqueeText" className="form-label fw-semibold text-dark fs-5">
                    <i className="bi bi-pencil-square me-2"></i>
                    Update Marquee Text:
                  </label>
                  <textarea
                    id="marqueeText"
                    className="form-control form-control-lg"
                    style={{ 
                      width: "100%", 
                      minHeight: "120px",
                      fontSize: "16px",
                      border: "2px solid #dee2e6",
                      borderRadius: "8px",
                      resize: "vertical",
                      padding: "12px"
                    }}
                    rows="4"
                    value={newMarqueeText}
                    onChange={(e) => setNewMarqueeText(e.target.value)}
                    placeholder="Enter your marquee text here... This text will scroll across the top of your website."
                    disabled={updating}
                  />
                  <div className="form-text text-muted mt-2">
                    <i className="bi bi-lightbulb me-1"></i>
                    This text will be displayed as a scrolling marquee on your website homepage.
                  </div>
                </div>

                <div className="d-flex gap-3 flex-wrap align-items-center">
                  <button 
                    type="submit" 
                    className="btn btn-primary btn-lg px-4 py-2"
                    disabled={updating || !newMarqueeText.trim()}
                    style={{
                      minWidth: '140px'
                    }}
                  >
                    {updating ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                        Updating...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-check-circle me-2"></i>
                        Update Marquee
                      </>
                    )}
                  </button>

                  {/* {marqueeText.trim() && (
                    <button 
                      type="button" 
                      className="btn btn-danger btn-lg px-4 py-2"
                      onClick={clearMarquee}
                      disabled={updating}
                      style={{
                        minWidth: '140px'
                      }}
                    >
                      {updating ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                          Clearing...
                        </>
                      ) : (
                        <>
                          <i className="bi bi-trash me-2"></i>
                          Clear Marquee
                        </>
                      )}
                    </button>
                  )} */}

                  <button 
                    type="button" 
                    className="btn btn-outline-secondary btn-lg px-4 py-2"
                    onClick={fetchMarqueeText}
                    disabled={updating}
                    style={{
                      minWidth: '120px'
                    }}
                  >
                    <i className="bi bi-arrow-clockwise me-2"></i>
                    Refresh
                  </button>
                </div>
              </form>

              {/* Stats Info */}
              {/* <div className="mt-4 pt-3 border-top">
                <div className="row text-center">
                  <div className="col-md-4">
                    <div className="text-muted small">
                      <i className="bi bi-text-paragraph me-1"></i>
                      Characters: {newMarqueeText.length}
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="text-muted small">
                      <i className="bi bi-clock me-1"></i>
                      Last Updated: Just now
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="text-muted small">
                      <i className="bi bi-eye me-1"></i>
                      Status: {marqueeText.trim() ? 'Active' : 'Inactive'}
                    </div>
                  </div>
                </div>
              </div> */}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default MyMarqueeAdmin;